import { startCase } from 'lodash';
import moment from 'moment';
import constants, { FILTER_DATA_TYPES } from '../../const/Const';
import { CoreUtils } from '../../utils/Utils';
import { Constants } from '../../const';

export const INITIAL_DOWNLOADS_FILTERS = {
  screening_type: 'o',
  dates: {
    start: moment().startOf('month'),
    end: moment(),
  },
};

export function getDownloadsFilters({
  user,
  analystType,
  teamList,
  updatedUsersList,
  country,
  teams,
  managers,
  ratings,
  tableFilters,
  downloadsFilters,
  industry,
  occupation,
  product,
  transactionType,
  productType,
  channel,
  screen,
}) {
  const { sortOptions, formatUserDisplayName } = CoreUtils;
  const { SEARCH_TYPES, SCREEN_TYPES, SEARCH_TYPES_TRANSACTION } =
    Constants.CommonConst;

  const managerIds =
    user?.role === 'manager'
      ? [user.id]
      : Array.isArray(managers)
      ? managers.map((id) => parseInt(id, 10))
      : [];

  // Transform filters to textFieldNames format
  const textFieldNames =
    Object.values(tableFilters ?? {})?.map((item) => ({
      label: item?.display_name,
      fieldName: item?.code_name,
    })) || [];

  const filters = [
    {
      key: 'screening_type',
      label: 'Screening Type',
      datatype: FILTER_DATA_TYPES.SINGLESELECT,
      hasStaticOptions: true,
      options: [
        { label: 'Initial Screening', value: SCREEN_TYPES.i.key },
        { label: 'Ongoing Monitoring', value: SCREEN_TYPES.o.key },
      ],
      isDefault: true,
      isRequired: true,
    },
    {
      key: 'alerted',
      label: 'Alert/Non-Alert',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options: [
        { label: 'Alert', value: 'Alert' },
        { label: 'Non-Alert', value: 'Non-Alert' },
      ],
    },
    {
      key: 'listType',
      label: 'Result Type',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options:
        downloadsFilters?.alerted?.includes('Non-Alert') &&
        downloadsFilters?.alerted?.length === 1
          ? [] // No side effects needed since we're using state instead of formik
          : [
              ...(user?.sanction_permission
                ? [{ label: 'Sanctions', value: 'Sanctions' }]
                : []),
              ...(user?.pep_permission ? [{ label: 'PEP', value: 'PEP' }] : []),
              ...(user?.ams_permission ? [{ label: 'AMS', value: 'AMS' }] : []),
              ...(user?.corporate_permission
                ? [{ label: 'Corporate', value: 'Corporate' }]
                : []),
              ...(user?.legal_permission
                ? [{ label: 'Legal', value: 'Legal' }]
                : []),
            ],
      isDefault: true,
    },
    {
      key: 'status',
      label: 'Status',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options: [
        { label: 'Accepted', value: 'Accepted' },
        { label: 'Rejected', value: 'Rejected' },
        { label: 'Pending', value: 'Pending' },
      ],
      isDefault: true,
    },
    {
      key: 'true_match',
      label: 'True Match',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options: [
        { label: 'Sanction True Match', value: 'sanction_true_match' },
        { label: 'PEP True Match', value: 'pep_true_match' },
        { label: 'AMS True Match', value: 'ams_true_match' },
      ],
      isDefault: true,
    },
    {
      key: 'dates',
      label: 'Initiated Date',
      datatype: FILTER_DATA_TYPES.DATE,
      hasStaticOptions: true,
      options: [],
      isDefault: true,
      isRequired: true,
      disableFuture: true,
      limitThisMonthToToday: true,
    },
    {
      key: 'due_date',
      label: 'Due Date',
      datatype: FILTER_DATA_TYPES.DATE,
      hasStaticOptions: true,
      options: [],
      isDefault: false,
      disableFuture: false,
    },
    {
      key: 'search_type',
      label: 'Search Type',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options: [
        {
          label: SEARCH_TYPES_TRANSACTION?.b?.label,
          value: SEARCH_TYPES_TRANSACTION?.b?.key,
        },
        { label: SEARCH_TYPES?.i?.label, value: SEARCH_TYPES?.i?.key },
        { label: SEARCH_TYPES?.e?.label, value: SEARCH_TYPES?.e?.key },
        { label: SEARCH_TYPES?.v?.label, value: SEARCH_TYPES?.v?.key },
        { label: SEARCH_TYPES?.a?.label, value: SEARCH_TYPES?.a?.key },
      ],
    },
  ];

  if (
    !(
      downloadsFilters?.search_type?.includes(SEARCH_TYPES?.v?.key) ||
      downloadsFilters?.search_type?.includes(SEARCH_TYPES?.a?.key)
    )
  ) {
    filters.push({
      key: 'risk_type',
      label: 'Risk Rating',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options: ratings?.map((r) => ({ label: startCase(r), value: r })) || [],
      isDefault: false,
    });
  }

  if (user?.role === 'admin') {
    filters.push({
      key: 'manager_id',
      label: 'Select Manager',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options:
        analystType?.length > 0
          ? (sortOptions(analystType) || [])
              .filter((opt) => opt.analyst_level === -1)
              .map((opt) => ({
                label: formatUserDisplayName(opt),
                value: opt.user_id || opt.id,
              }))
          : [],
      isDefault: false,
    });
  }

  const managerUsernames =
    user?.role === 'manager'
      ? [user.username]
      : Array.isArray(managers) && updatedUsersList?.length > 0
      ? updatedUsersList
          .filter((u) => managers.includes(u.user_id || u.id))
          .map((u) => u.username)
      : [];

  filters.push(
    {
      key: 'team',
      label: 'Select Team',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options: [
        { label: 'Manual', value: 'manual' },
        ...(teamList?.length > 0
          ? (sortOptions(teamList) || [])
              .filter((opt) => {
                if (managerIds.length === 0 && user?.role === 'admin') {
                  return true;
                }
                if (user?.role === 'maker' || user?.role === 'checker') {
                  return opt?.analysts?.some(
                    (analyst) => analyst.username === user?.username
                  );
                }

                // Check if manager created the team or is a member
                const creatorUsername =
                  opt?.created_by?.username || opt?.created_by;
                const isCreator = managerUsernames.includes(creatorUsername);
                const isMember = opt?.analysts?.some((analyst) =>
                  managerUsernames.includes(analyst.username)
                );

                return isCreator || isMember;
              })
              .map((opt) => ({
                label: `${opt?.name} (${opt?.id})`,
                value: opt?.id,
              }))
          : []),
      ],
      isDefault: false,
    },
    {
      key: 'analyst_id',
      label: 'Select Analyst (Reviewed by)',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options: (() => {
        if (teams && teams.length === 1 && teams[0] === 'manual') {
          return [
            { label: 'No Option Available', value: null, disabled: true },
          ];
        }
        let sourceList = updatedUsersList;

        if (managerIds.length > 0 || managerUsernames.length > 0) {
          // Get analysts from teams where manager is creator OR member
          const managerTeams = (teamList || []).filter((t) => {
            const creator = t?.created_by?.username || t?.created_by;
            const isCreator = managerUsernames.includes(creator);
            const isMember = t?.analysts?.some((a) =>
              managerUsernames.includes(a.username)
            );
            return isCreator || isMember;
          });

          const allAnalysts = managerTeams.flatMap((t) => t.analysts || []);
          const seenIds = new Set();
          sourceList = allAnalysts.filter((analyst) => {
            const id = analyst?.id;
            if (!id || seenIds.has(id)) return false;
            seenIds.add(id);
            return true;
          });
        }

        // Filter by selected teams
        if (teams && teams.length > 0) {
          const selectedTeams = teams.filter((tId) => tId !== 'manual');
          if (selectedTeams.length > 0) {
            const teamAnalysts = (teamList || [])
              .filter((t) => selectedTeams.includes(t.id))
              .flatMap((t) => t.analysts || []);

            const seenIds = new Set();
            sourceList = teamAnalysts.filter((analyst) => {
              const id = analyst?.id;
              if (!id || seenIds.has(id)) return false;
              seenIds.add(id);
              return true;
            });
          }
        }

        const finalOptions = sortOptions(sourceList || []).map((opt) => ({
          label: formatUserDisplayName(opt),
          value: opt?.user_id || opt?.id,
        }));

        return finalOptions.length > 0
          ? finalOptions
          : [{ label: 'No analysts available', value: null, disabled: true }];
      })(),
      isDefault: false,
    },
    {
      key: 'created_by_id',
      label: 'Created By',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options:
        updatedUsersList?.length > 0
          ? (
              sortOptions(
                updatedUsersList?.filter(
                  (_user, index, self) =>
                    index === self?.findIndex((u) => u?.id === _user?.id)
                ) || []
              ) || []
            ).map((opt) => ({
              label: formatUserDisplayName(opt),
              value: opt?.user_id || opt?.id,
            }))
          : [],
      isDefault: false,
    }
  );

  if (downloadsFilters?.status?.includes('Pending')) {
    filters.push({
      key: 'pending_with',
      label: 'Pending With',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options:
        updatedUsersList?.length > 0
          ? (
              sortOptions(
                updatedUsersList?.filter(
                  (_user, index, self) =>
                    index === self?.findIndex((u) => u?.id === _user?.id)
                ) || []
              ) || []
            ).map((opt) => ({
              label: formatUserDisplayName(opt),
              value: opt?.user_id || opt?.id,
            }))
          : [],
      isDefault: false,
    });
  }

  // Helper function to transform array to options format
  const createOptions = (items, valueTransform = (item) => item) =>
    items?.map((item) => ({
      label: startCase(item),
      value: valueTransform(item),
    })) || [];

  const optionsMap = {
    'Risk Rating': createOptions(ratings, (r) => r),
    'Channel Type': createOptions(channel?.values),
    Country: country || [],
    Industry: createOptions(industry),
    Occupation: createOptions(occupation),
    'Risk Rating Product Type': createOptions(product),
    'Transaction Type': createOptions(transactionType?.values),
    'Transaction Product Type': createOptions(productType?.values),
    'Onboarding Type': createOptions(constants?.ONBOARDING_TYPE),
  };

  const otherFilters = textFieldNames?.map((item) => ({
    key: item?.fieldName || item?.label?.toLowerCase().replace(/\s+/g, '_'),
    label: item?.label,
    datatype: FILTER_DATA_TYPES.MULTISELECT,
    hasStaticOptions: true,
    options: optionsMap[item?.label] ?? [],
    isDefault: false,
  }));

  if (otherFilters?.length) {
    filters.push(...otherFilters);
  }

  filters.push({
    key: 'search_source',
    label: 'Search Source',
    datatype: FILTER_DATA_TYPES.MULTISELECT,
    hasStaticOptions: true,
    options: [
      { label: 'Reassigned', value: 'assigned' },
      { label: 'Referred', value: 'referred' },
      { label: 'Transferred', value: 'transferred' },
      ...(screen === 'o'
        ? [{ label: 'Daily Monitoring', value: 'daily_monitoring' }]
        : []),
    ],
    isDefault: false,
  });

  return filters;
}

